/**
 * Voxly — MusicPicker (shared).
 *
 * Bottom-sheet dialog backed by /api/music endpoints (iTunes Search API).
 *  - Genre rails (Trending, Pop, Hip-Hop, Lofi, Acoustic, Rock, EDM, …)
 *  - Free-text search box (real artists / songs)
 *  - Tap artwork to preview the 30-second clip; tap Pick to select.
 *
 * Props:
 *   open       boolean
 *   onClose    () => void
 *   onPick     (track) => void   — track shape from /music/library
 *   selectedId string?           — current track id, for highlighting
 */
import React, { useEffect, useRef, useState } from "react";
import { X, MagnifyingGlass, Stop, Play } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import logger from "@/lib/logger";
import { toast } from "sonner";

const FALLBACK_GENRES = [
  { id: "trending", label: "Trending now", emoji: "🔥" },
  { id: "pop",       label: "Pop",          emoji: "💖" },
  { id: "hiphop",    label: "Hip-Hop",      emoji: "🎧" },
  { id: "lofi",      label: "Lo-Fi",        emoji: "📻" },
  { id: "acoustic",  label: "Acoustic",     emoji: "🪕" },
  { id: "rock",      label: "Rock",         emoji: "🎸" },
  { id: "edm",       label: "EDM",          emoji: "🎛️" },
  { id: "rnb",       label: "R&B",          emoji: "🎷" },
  { id: "cinematic", label: "Cinematic",    emoji: "🎬" },
  { id: "workout",   label: "Workout",      emoji: "🏋️" },
];

export default function MusicPicker({ open, onClose, onPick, selectedId }) {
  const [genres, setGenres] = useState(FALLBACK_GENRES);
  const [activeGenre, setActiveGenre] = useState("trending");
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [searchMode, setSearchMode] = useState(false);
  const [previewingId, setPreviewingId] = useState(null);
  const previewRef = useRef(null);
  const searchTimerRef = useRef(null);
  // Cache fetched tracks per genre so switching back is instant.
  const genreCacheRef = useRef(new Map());

  // Pause preview audio when picker closes.
  useEffect(() => {
    if (!open && previewRef.current) {
      previewRef.current.pause();
      previewRef.current = null;
      setPreviewingId(null);
    }
  }, [open]);

  // Load genre list once.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/music/genres");
        if (Array.isArray(data?.genres) && data.genres.length) setGenres(data.genres);
      } catch (err) { logger.warn("music-picker", "genres fetch failed", err); }
    })();
  }, []);

  // Load tracks for the active genre (browse mode only). Cache results so the
  // user can flip between rails without paying iTunes-API latency every time.
  useEffect(() => {
    if (!open || searchMode) return;
    let cancelled = false;
    const cached = genreCacheRef.current.get(activeGenre);
    if (cached) {
      setTracks(cached);
      setLoading(false);
      return;
    }
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/music/library?genre=${activeGenre}&limit=30`);
        if (cancelled) return;
        const list = Array.isArray(data?.tracks) ? data.tracks : [];
        genreCacheRef.current.set(activeGenre, list);
        setTracks(list);
      } catch (err) {
        logger.warn("music-picker", "library fetch failed", err);
        if (!cancelled) setTracks([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activeGenre, open, searchMode]);

  // Debounced search.
  useEffect(() => {
    if (!searchMode) return;
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    const q = query.trim();
    if (!q) { setTracks([]); setLoading(false); return; }
    setLoading(true);
    searchTimerRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/music/search?q=${encodeURIComponent(q)}&limit=30`);
        setTracks(Array.isArray(data?.tracks) ? data.tracks : []);
      } catch (err) {
        logger.warn("music-picker", "search failed", err);
        setTracks([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => { if (searchTimerRef.current) clearTimeout(searchTimerRef.current); };
  }, [query, searchMode]);

  const togglePreview = (track) => {
    if (previewRef.current) { previewRef.current.pause(); previewRef.current = null; }
    if (previewingId === track.id) { setPreviewingId(null); return; }
    const a = new Audio(track.url); a.volume = 0.7;
    a.play().then(() => setPreviewingId(track.id)).catch((err) => {
      logger.warn("music-picker", "preview failed", err);
      toast.error("Couldn't play this preview");
      setPreviewingId(null);
    });
    a.onended = () => setPreviewingId(null);
    previewRef.current = a;
  };

  const pick = (track) => {
    if (previewRef.current) previewRef.current.pause();
    setPreviewingId(null);
    onPick(track);
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[110] bg-black/75 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="music-picker" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-card border border-border rounded-t-3xl sm:rounded-3xl w-full max-w-lg max-h-[85vh] flex flex-col">
        <div className="p-4 flex items-center justify-between border-b border-border">
          <div>
            <h3 className="font-heading text-lg font-bold">Pick music</h3>
            <p className="text-xs text-muted-foreground">Real songs · 30s preview</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground" data-testid="music-picker-close" aria-label="Close">
            <X size={22} />
          </button>
        </div>

        {/* Search bar */}
        <div className="p-3 border-b border-border">
          <div className="flex items-center gap-2 bg-secondary/60 rounded-2xl px-3 py-2">
            <MagnifyingGlass size={18} className="text-muted-foreground shrink-0" />
            <input
              type="text"
              value={query}
              onChange={(e) => { setQuery(e.target.value); setSearchMode(true); }}
              onFocus={() => setSearchMode(true)}
              placeholder="Search song or artist…"
              className="bg-transparent flex-1 outline-none text-sm placeholder:text-muted-foreground"
              data-testid="music-picker-search"
            />
            {query && (
              <button onClick={() => { setQuery(""); setSearchMode(false); }} className="text-muted-foreground hover:text-foreground" data-testid="music-picker-clear">
                <X size={16} />
              </button>
            )}
          </div>
        </div>

        {/* Genre rails */}
        {!searchMode && (
          <div className="px-2 pt-3 pb-2 border-b border-border overflow-x-auto" data-testid="music-genre-rail">
            <div className="flex gap-2 px-1">
              {genres.map((g) => {
                const active = g.id === activeGenre;
                return (
                  <button
                    key={g.id}
                    onClick={() => setActiveGenre(g.id)}
                    data-testid={`music-genre-${g.id}`}
                    className={`shrink-0 px-3 py-2 rounded-full text-xs font-semibold transition border ${active ? "bg-primary text-primary-foreground border-primary" : "bg-secondary border-border text-foreground hover:bg-secondary/80"}`}
                  >
                    <span className="mr-1">{g.emoji}</span>{g.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Track list */}
        <div className="flex-1 overflow-y-auto p-2 space-y-1.5" data-testid="music-track-list">
          {loading ? (
            <div className="space-y-2 p-2">
              {[0, 1, 2, 3, 4].map((i) => (
                <div key={i} className="h-16 rounded-xl bg-secondary/40 animate-pulse" />
              ))}
            </div>
          ) : tracks.length === 0 ? (
            <p className="text-center text-sm text-muted-foreground py-12 px-4">
              {searchMode ? (query.trim() ? "Nothing found. Try a different name." : "Type a song or artist above.") : "No tracks loaded."}
            </p>
          ) : (
            tracks.map((t) => {
              const isPlaying = previewingId === t.id;
              const isSelected = selectedId === t.id;
              return (
                <div
                  key={t.id}
                  className={`flex items-center gap-3 p-2 rounded-xl ${isSelected ? "bg-primary/15 border border-primary/40" : "hover:bg-secondary/50 border border-transparent"}`}
                  data-testid={`music-track-${t.id}`}
                >
                  <button
                    onClick={() => togglePreview(t)}
                    aria-label={isPlaying ? "Stop preview" : "Play preview"}
                    data-testid={`music-preview-${t.id}`}
                    className="relative shrink-0 h-14 w-14 rounded-xl overflow-hidden bg-secondary border border-border active:scale-95 transition"
                  >
                    {t.artwork ? (
                      <img
                        src={t.artwork.replace("300x300", "100x100")}
                        alt=""
                        className="absolute inset-0 w-full h-full object-cover opacity-0 transition-opacity duration-200"
                        loading="lazy"
                        decoding="async"
                        onLoad={(e) => { e.currentTarget.style.opacity = "1"; }}
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center text-2xl">🎵</div>
                    )}
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center text-white">
                      {isPlaying ? <Stop size={20} weight="fill" /> : <Play size={18} weight="fill" />}
                    </div>
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate" title={t.title}>{t.title}</div>
                    <div className="text-xs text-muted-foreground truncate" title={t.artist}>
                      {t.artist}{t.genre ? ` · ${t.genre}` : ""}
                    </div>
                  </div>
                  <button
                    onClick={() => pick(t)}
                    data-testid={`music-pick-${t.id}`}
                    className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold transition ${isSelected ? "bg-primary text-primary-foreground" : "bg-secondary hover:bg-primary hover:text-primary-foreground border border-border"}`}
                  >
                    {isSelected ? "Selected" : "Pick"}
                  </button>
                </div>
              );
            })
          )}
        </div>

        <div className="p-3 border-t border-border text-[11px] text-muted-foreground text-center">
          Music previews from Apple iTunes · Used under fair-use 30-second preview
        </div>
      </div>
    </div>
  );
}
